import {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  UniqueIdentifier,
  DragEndEvent,
  DragOverEvent,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useDispatch, useSelector } from 'react-redux';
import { useTimeout } from 'Context/TimeoutContext';
import { ActivitiesActions } from 'Store/Activities/Activities.actions';
import { getTheme } from 'Store/MultiDomain/MultiDomain.selector';
import { useQueryUpdatedDate } from 'Hooks/activities/useQueryUpdatedDate';
import {
  IDragAndDropDataFormat,
  ITopicDnDResponse,
  ITopicStatusDnDFormat,
} from 'Data/interfaces/Activities/IDragAndDropDataFormat';
import { IUpdateListRequest } from 'Data/interfaces/Activities/IUpdateList';
import {
  IChangeStatus,
  IInitialListUpdateRequest,
} from 'Data/interfaces/Activities/IChangeStatus';
import {
  IFiltroOptions,
  enumToStringValueMap,
} from 'Data/interfaces/Activities/IFilters';
import { INotificationInAppTopic } from 'Data/interfaces/Notification/INotification';
import Column from './components/Column';
import Item from './components/Item';
import {
  complexfilterActivities,
  filterActivities,
  sortActivities,
} from 'Utils/ActivitiesUtils';
import { IClearFiltersRequestUpdatedDate } from 'Data/interfaces/Activities/IFiltersRequest';
import dayjs from 'dayjs';

interface IKanbanVision {
  csId: number;
  listActivities?: IDragAndDropDataFormat;
  height: number;
  searchActivity: string;
  endStatus?: IChangeStatus;
  showFilters?: boolean;
  filters?: Record<string, IFiltroOptions<ITopicDnDResponse>>;
  hasFilters: boolean;
  inAppTopicNotifications: INotificationInAppTopic[];
  onOpenCreateActivity: () => void;
  onOpenShowActivity: (activity: string) => void;
}

interface IColumnCreate {
  column: ITopicStatusDnDFormat;
  filteredActivities: ITopicDnDResponse[];
}
interface IColumnCreateObj {
  columns: Record<string, IColumnCreate>
  totalFilteredAllItems: number;
}

const KanbanVision = ({
  csId,
  listActivities,
  height,
  searchActivity,
  endStatus,
  showFilters,
  filters,
  hasFilters,
  inAppTopicNotifications,
  onOpenCreateActivity,
  onOpenShowActivity,
}: IKanbanVision) => {
  const dispatch = useDispatch();
  const { set, clear } = useTimeout();
  const theme = useSelector(getTheme);

  const filterUpdatedDate = useMemo(() => {
    const filterData: IClearFiltersRequestUpdatedDate = { from: null, to: null };
    if (!filters) return filterData;
    
    const filterArray = Object.values(filters);
    const updatedDateFilter = filterArray.findIndex(filter => {
      return filter.topicId === 'UpdatedDate';
    });
    if (updatedDateFilter !== -1) {
      const filter = filterArray[updatedDateFilter];
      let value1 = null;
      let value2 = null;
      if (Array.isArray(filter.value) && filter.value.length > 1) {
        value1 = dayjs(filter.value[0]);
        value2 = dayjs(filter.value[1]);
      } else {
        value1 = dayjs(filter.value);
      }
      switch (filter.operation) {
        case 'equal':
          filterData.from = value1.format('YYYY-MM-DD');
          filterData.to = value1.format('YYYY-MM-DD');
          break;
        case 'between':
          if (value1 && value2) {
            filterData.from = value1.format('YYYY-MM-DD');
            filterData.to = value2.format('YYYY-MM-DD');
          }
          break;
        case 'before':
          filterData.to = value1.subtract(1, 'day').format('YYYY-MM-DD');
          break;
        case 'after':
          filterData.from = value1.add(1, 'day').format('YYYY-MM-DD');
          break;
        case 'onOrBefore':
          filterData.to = value1.format('YYYY-MM-DD');
          break;
        case 'onOrAfter':
          filterData.from = value1.format('YYYY-MM-DD');
          break;
      }
    }
    return filterData;
  }, [filters]);
  const { 
    data: dataUpdatedDate, 
    isFetching: isFetchingUpdatedDate 
  } = useQueryUpdatedDate({ 
    csId, 
    from: filterUpdatedDate.from, 
    to: filterUpdatedDate.to 
  });

  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [activeColumn, setActiveColumn] = useState<ITopicStatusDnDFormat | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const wrapperStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
  };

  useEffect(() => {
    if (endStatus) {
      dispatch(ActivitiesActions.changeActivityStatus(endStatus));
    }
  }, [endStatus]);

  useEffect(() => {
    return () => {
      clear('KanbanVision-handleNewData');
    }
  }, []);

  const handleNewData = useCallback((newData: IUpdateListRequest) => {
    if (newData.provider === 'end') {
      dispatch(ActivitiesActions.listActivitiesUpdate(newData));
    } else {
      clear('KanbanVision-handleNewData');
      set('KanbanVision-handleNewData', () => {
        dispatch(ActivitiesActions.listActivitiesUpdate(newData));
      }, 50);
    }
  }, [dispatch, set, clear]);

  const findContainer = useCallback((id: UniqueIdentifier) => {
    if (!id) return;
    if (!listActivities) return;

    if (listActivities.columns.hasOwnProperty(id)) {
      return id;
    }

    const column = listActivities.columnOrder.find(columnId => {
      return listActivities.columns[columnId].taskIds.includes(id.toString());
    })
    return column;
  }, [listActivities]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const { active } = event;
    const { id } = active;

    if (listActivities?.columns && listActivities?.activities && id) {
      const activeColumnStatus = listActivities.activities[id].Status?.TopicStatusId;
      const getActiveColumn = listActivities.columns[`column-${activeColumnStatus}` || '']

      const activeColumnTaskIds = Array.from(getActiveColumn.taskIds);
      const activitieIndex = activeColumnTaskIds.indexOf(id.toString());

      setActiveColumn(getActiveColumn);

      const initialStatus: IInitialListUpdateRequest = {
        currentId: id,
        initialIndex: activitieIndex,
        initialContainer: activeColumnStatus,
      };
      dispatch(ActivitiesActions.initialChangeActivityStatus(initialStatus));
    }

    return setActiveId(id);
  }, [listActivities]);

  const handleDragOver = useCallback((event: DragOverEvent) => {
    if (!listActivities) return;

    const { active, over, delta } = event;

    if (!over) {
      return;
    }

    const { id } = active;
    const { id: overId } = over;

    const activeContainer = findContainer(id);
    const overContainer = findContainer(overId);

    if (
      !activeContainer ||
      !overContainer ||
      activeContainer === overContainer
    ) {
      return;
    }

    const data: IUpdateListRequest = {
      provider: 'over',
      active,
      activeContainer,
      over,
      overContainer,
      delta,
    };

    return handleNewData(data);
  }, [handleNewData, listActivities, findContainer]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    if (!listActivities) return;

    const { active, over } = event;

    if (!over) {
      return;
    }

    const { id } = active;
    const { id: overId } = over;

    const activeContainer = findContainer(id);
    const overContainer = findContainer(overId);

    if (
      !activeContainer ||
      !overContainer ||
      activeContainer !== overContainer
    ) {
      return;
    }

    const data: IUpdateListRequest = {
      provider: 'end',
      active,
      activeContainer,
      over,
      overContainer,
    };

    handleNewData(data);
    return setActiveId(null);
  }, [handleNewData, listActivities, findContainer]);

  const columnObj = useCallback(() => {
    const lookupColumn = {
      columns: {},
      totalFilteredAllItems: 0,
    } as IColumnCreateObj;

    listActivities?.columnOrder?.map(columnId => {
      const column = listActivities.columns[columnId];
      const activities = column.taskIds?.map(taskId => listActivities.activities[taskId]);
      const clearActivities = activities.filter(activity => activity);

      const sortedActivities = clearActivities.sort(sortActivities);

      const filteredArray = complexfilterActivities<ITopicDnDResponse, typeof enumToStringValueMap>({
        filters,
        array: sortedActivities,
        enumToStringValueMap,
        updatedDateIds: isFetchingUpdatedDate ? undefined : dataUpdatedDate,
      });

      const filteredActivities = filterActivities(filteredArray, searchActivity);

      lookupColumn.totalFilteredAllItems += filteredActivities.length;
      return lookupColumn.columns[columnId] = {
        column,
        filteredActivities,
      }
    })

    return lookupColumn;
  }, [
    listActivities,
    searchActivity,
    filters,
    dataUpdatedDate,
    isFetchingUpdatedDate,
  ]);

  return (
    <div style={wrapperStyle}>
      <DndContext
        key="DnDContextKey"
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={(event) => handleDragStart(event)}
        onDragOver={(event) => handleDragOver(event)}
        onDragEnd={(event) => handleDragEnd(event)}
      >
        {listActivities?.columnOrder?.map(columnId => {
          const currentColumn = columnObj().columns[columnId];
          const totalFilteredAllItems = columnObj().totalFilteredAllItems;

          return (
            <Column
              key={currentColumn.column.TopicStatusId}
              height={height}
              id={`column-${currentColumn.column.TopicStatusId}`}
              activeId={activeId}
              items={currentColumn.filteredActivities.map(act => act.Guid)}
              column={currentColumn.column}
              title={currentColumn.column.Name}
              activitiesList={currentColumn.filteredActivities}
              totalItems={currentColumn.filteredActivities.length}
              totalAllItems={Object.keys(listActivities.activities).length}
              totalFilteredAllItems={totalFilteredAllItems}
              searchActivity={searchActivity}
              showFilters={showFilters}
              hasFilters={hasFilters}
              inAppTopicNotifications={inAppTopicNotifications}
              onOpenCreateActivity={onOpenCreateActivity}
              onOpenShowActivity={onOpenShowActivity}
            />
          )
        })}
        <DragOverlay>
          {activeId ? (
            <Item
              activeId={activeId}
              activity={listActivities?.activities[activeId] || {} as ITopicDnDResponse}
              column={activeColumn || {} as ITopicStatusDnDFormat}
              searchActivity={searchActivity}
              theme={theme}
              inAppTopicNotifications={inAppTopicNotifications}
            />
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
}

export default KanbanVision;
